import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  Input,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  LinkText,
  AuthFormContainer,
  AuthCardEllipse,
  AuthCardTopFrame,
  AuthHeading,
  AuthSubheading,
  AuthFieldsWrapper,
  AuthButton,
  AuthButtonWrapper,
  AuthCheckboxText,
} from "@root/commonStyles/commonStyle";
import { LoginBrandingLogo, LoginBrandingTitle } from "@root/components/branding";
import { ROUTE_NAMES, TOAST_VARIANT } from "@root/constants";
import { updatePasswordRecoverEmail } from "@root/store/slices/entities";
import { showAlert } from "@root/store/slices/features";
import { login, signup } from "@root/store/thunks";
import { Field, Form, Formik } from "formik";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { FloatingInput } from "../login/floating-input";
import { useCountryContext } from "@root/context/use-country-context";
import { LanguageToggle } from "@root/components/dashboard/sidebar/languagetoggler";
import { useBrandingContext } from "@root/context/branding-context";

interface SignupValues {
  email: string;
  isConsant: boolean;
}

export const Signup: React.FC = () => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { country } = useCountryContext();
  const { organizationId } = useBrandingContext();

  const initialValues = {
    email: "",
    isConsant: false,
  };

  const onSuccessSocial = (res: any) => {
    if (!res?.token) {
      return;
    }
    setLoading(false);
    navigate(ROUTE_NAMES.DASHBOARD);
  };

  const onErrorSocial = () => { };

  const onSuccessHandler = (credentialResponse: any) => {
    const payload = { token: credentialResponse?.credential };
    dispatch(
      login({
        payload,
        isSocial: true,
        onSuccess: onSuccessSocial,
        onError: onErrorSocial,
      })
    );
  };

  const onSuccess = (email: any) => {
    setLoading(false);
    navigate(ROUTE_NAMES.EMAIL_VERIFICATION);
    dispatch(
      updatePasswordRecoverEmail({
        email,
      })
    );
  };
  const onError = () => {
    setLoading(false);
  };

  // const validate = (values: SignupValues) => {
  //   const errors: Partial<SignupValues> = {};

  //   if (!values.password) {
  //     errors.password = t("login.requiredPassword");
  //   } else if (values.password.length < 6) {
  //     errors.password = t("login.passwordValidation");
  //   }
  //   return errors;
  // };

  const onSubmit = (values: any) => {
    if (!values.isConsant) {
      dispatch(
        showAlert({
          message: t("signup.acceptStatus"),
          type: TOAST_VARIANT.ERROR,
        })
      );
      return;
    }
    setLoading(true);
    try {
      const payload = {
        email: values.email,
        ip_address: country?.IPv4,
        orgId: organizationId,
      };
      dispatch(
        signup({
          payload,
          onSuccess: () => onSuccess(values?.email),
          onError,
        })
      );
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Flex
      minH="100vh"
      width="100%"
      bg="#F5F5F5"
      align="center"
      justify="center"
    >
      <Box position="relative">
        <Box position="absolute" top="0" right="0" zIndex={1000}>
          <LanguageToggle variant="public" position="relative" />
        </Box>
        <AuthFormContainer minWidth={{ base: "auto", md: "550px" }}>
          <AuthCardEllipse />

          {/* Top Section */}
          <AuthCardTopFrame>
            <LoginBrandingLogo />
            <LoginBrandingTitle />
          </AuthCardTopFrame>

          {/* Form */}
          <Formik initialValues={initialValues} onSubmit={onSubmit}>
            {({ handleSubmit, errors, touched, values, setFieldValue }) => (
              <form onSubmit={handleSubmit} style={{ width: "100%" }}>
                <AuthFieldsWrapper>
                  <Flex
                    direction="column"
                    align="flex-start"
                    gap="1rem"
                    width="100%"
                  >
                    {/* Email Input */}
                    <Flex direction="column" gap="0.5rem" width="100%">
                      <FloatingInput
                        id="email"
                        label={t("login.emailAddress")}
                        name="email"
                        type="email"
                        value={values.email}
                        error={errors.email}
                        touched={touched.email}
                        onChange={(e) => setFieldValue("email", e.target.value)}
                      />
                    </Flex>

                    {/* Terms & Conditions Checkbox */}
                    <Flex align="center" width="100%" mt={2} gap="0.5rem">
                      <Field
                        as={Checkbox}
                        id="isConsant"
                        name="isConsant"
                        iconSize="18px"
                        size="lg"
                        borderRadius="50%"
                        sx={{
                          ".chakra-checkbox__control": {
                            borderRadius: "50%",
                            width: "20px",
                            height: "20px",
                            border: "1px solid #000",
                          },
                          ".chakra-checkbox__control[data-checked]": {
                            backgroundColor: "#000",
                            borderColor: "#000",
                          },
                          ".chakra-checkbox__control[data-checked] svg": {
                            color: "#fff",
                          },
                        }}
                      />
                      <AuthCheckboxText>
                        {`${t("signup.creatingAccount")} `}
                        <Link to={ROUTE_NAMES.PRIVACY_POLICY}>
                          <Text
                            as="span"
                            fontWeight={700}
                            color={"#0D0D0D"}
                            textDecoration="underline"
                          >
                            {t("signup.privacyPolicy")}
                          </Text>
                        </Link>
                        {` ${t("signup.and")} `}
                        <Link to={ROUTE_NAMES.TERMS_CONDITION}>
                          <Text
                            as="span"
                            fontWeight={700}
                            color={"#0D0D0D"}
                            textDecoration="underline"
                          >
                            {t("signup.terms")}
                          </Text>
                        </Link>
                        {t("signup.last")}
                      </AuthCheckboxText>
                    </Flex>
                  </Flex>
                </AuthFieldsWrapper>

                {/* Button */}
                <AuthButtonWrapper>
                  <AuthButton
                    isLoading={loading}
                    isDisabled={!values.email || !values.isConsant}
                    bg={!values.email || !values.isConsant ? "#EBEBEB" : "black"}
                    color={
                      !values.email || !values.isConsant ? "#BFBFBF" : "#FFFFFF"
                    }
                    _hover={{
                      bg:
                        !values.email || !values.isConsant
                          ? "#dbd9d9ff"
                          : "#3d3535ff",
                    }}
                    type="submit"
                  >
                    {t("signup.signupBtn")}
                  </AuthButton>
                </AuthButtonWrapper>
              </form>
            )}
          </Formik>

          {/* Login link */}
          <Flex justify="center" alignItems={"center"} gap="0.25rem" width="100%">
            <AuthCheckboxText>{t("signup.alreadyAccount")}</AuthCheckboxText>
            <LinkText
              to="/login"
              color="#3d3535ff"
              style={{ textDecoration: "underline" }}
            >
              {t("login.loginLink")}
            </LinkText>
          </Flex>
        </AuthFormContainer>
      </Box>
    </Flex>
  );
};
